import React, { useEffect, useState } from 'react';
import {
  Autocomplete, Box, Button, Card, CardContent, Chip, MenuItem, Pagination, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { FileDownload } from '@mui/icons-material';
import api from '../services/api';

// Historial de movimientos de stock (1.2.b): productos terminados y materias
// primas, con quién, cuánto, por qué y saldo antes/después. Sirve también como
// auditoría de ajustes manuales (filtrar por motivo).

type Row = {
  id: number;
  productName?: string;
  materialName?: string;
  userName?: string | null;
  type: string;
  reason: string;
  quantity: number;
  previousStock: number;
  newStock: number;
  location?: string | null;
  lotCode?: string | null;
  referenceNumber?: string | null;
  notes?: string | null;
  createdAt: string;
};

const fmtDateTime = (iso: string) => {
  const d = new Date(iso);
  return `${d.toLocaleDateString('es-AR')} ${d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}`;
};

// Traducción amigable de los motivos internos; lo demás se muestra tal cual.
const REASON_LABELS: Record<string, string> = {
  PURCHASE: 'Recepción de compra',
  PRODUCTION_RESERVATION: 'Reserva de producción',
  RESERVATION_RELEASED: 'Reserva liberada',
  PRODUCTION_CONSUMPTION: 'Consumo de producción',
  PRODUCTION_ORDER: 'Producción terminada',
  BATCH_MANUAL: 'Alta manual de lote',
  BATCH_DELETED: 'Lote eliminado',
  BATCH_EDIT: 'Corrección de lote',
  BULK_IMPORT_ADJUSTMENT: 'Importación masiva',
  GRINDING_OUT: 'Salida a molienda',
  GRINDING_RETURN: 'Retorno de molienda',
};
const reasonLabel = (r: string) => REASON_LABELS[r] || r;

export const StockMovementsPage: React.FC = () => {
  const [tab, setTab] = useState<'materials' | 'products'>('materials');
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [exporting, setExporting] = useState(false);

  // Filtros
  const [typeFilter, setTypeFilter] = useState('');
  const [reasonFilter, setReasonFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [itemOptions, setItemOptions] = useState<{ id: number; name: string }[]>([]);
  const [itemFilter, setItemFilter] = useState<{ id: number; name: string } | null>(null);
  const [itemSearch, setItemSearch] = useState('');

  // Opciones del selector de producto/material (búsqueda server-side para productos).
  useEffect(() => {
    const handle = setTimeout(async () => {
      try {
        if (tab === 'products') {
          const r = await api.getProducts({ page: 1, pageSize: 30, search: itemSearch || undefined });
          setItemOptions((r.products || []).map((p: any) => ({ id: p.id, name: p.name })));
        } else {
          const r = await api.getMaterials({ page: 1, pageSize: 500, search: itemSearch || undefined });
          setItemOptions((r.materials || []).map((m: any) => ({ id: m.id, name: m.name })));
        }
      } catch { setItemOptions([]); }
    }, 250);
    return () => clearTimeout(handle);
  }, [tab, itemSearch]);

  const buildParams = (p: number) => ({
    page: p,
    pageSize: 25,
    type: typeFilter || undefined,
    reason: reasonFilter || undefined,
    from: fromDate || undefined,
    to: toDate || undefined,
  });

  const load = async (p = 1) => {
    setLoading(true);
    try {
      const res = tab === 'materials'
        ? await api.getMaterialMovements({ ...buildParams(p), materialId: itemFilter?.id })
        : await api.getInventoryMovements({ ...buildParams(p), productId: itemFilter?.id });
      setRows(res.items || []);
      setTotalPages(res.totalPages || 1);
      setTotalCount(res.totalCount || 0);
      setPage(p);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { setItemFilter(null); setRows([]); load(1); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const blob = tab === 'materials'
        ? await api.downloadMaterialMovementsCsv({ materialId: itemFilter?.id, type: typeFilter || undefined, reason: reasonFilter || undefined, from: fromDate || undefined, to: toDate || undefined })
        : await api.downloadMovementsCsv({ productId: itemFilter?.id });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = tab === 'materials' ? 'movimientos_materias_primas.csv' : 'movimientos_productos.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }}>
        <Typography variant="h4" fontWeight={700}>Movimientos de stock</Typography>
        <Button variant="outlined" startIcon={<FileDownload />} onClick={exportCsv} disabled={exporting}>
          {exporting ? 'Exportando…' : 'Exportar CSV'}
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        Todo cambio de stock queda acá: quién lo hizo, cuánto, por qué y el saldo antes y después.
        Para auditar ajustes manuales, filtrá por motivo (ej. "ajuste").
      </Typography>

      <Stack direction="row" spacing={1}>
        <Button size="small" variant={tab === 'materials' ? 'contained' : 'outlined'} onClick={() => setTab('materials')}>Materias primas</Button>
        <Button size="small" variant={tab === 'products' ? 'contained' : 'outlined'} onClick={() => setTab('products')}>Productos</Button>
      </Stack>

      <Card>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            <Autocomplete
              size="small"
              options={itemOptions}
              getOptionLabel={o => o.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={itemFilter}
              onChange={(_, v) => setItemFilter(v)}
              onInputChange={(_, v) => setItemSearch(v)}
              renderInput={params => <TextField {...params} label={tab === 'materials' ? 'Materia prima' : 'Producto'} />}
              sx={{ minWidth: 260 }}
            />
            <TextField size="small" select label="Tipo" value={typeFilter} onChange={e => setTypeFilter(e.target.value)} sx={{ minWidth: 130 }}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="IN">Entrada</MenuItem>
              <MenuItem value="OUT">Salida</MenuItem>
            </TextField>
            <TextField size="small" label="Motivo contiene" value={reasonFilter} onChange={e => setReasonFilter(e.target.value)} sx={{ minWidth: 170 }} />
            <TextField size="small" type="date" label="Desde" InputLabelProps={{ shrink: true }} value={fromDate} onChange={e => setFromDate(e.target.value)} sx={{ minWidth: 150 }} />
            <TextField size="small" type="date" label="Hasta" InputLabelProps={{ shrink: true }} value={toDate} onChange={e => setToDate(e.target.value)} sx={{ minWidth: 150 }} />
            <Button variant="outlined" onClick={() => load(1)}>Filtrar</Button>
          </Stack>

          {loading ? (
            <Typography variant="body2" color="text.secondary">Cargando…</Typography>
          ) : (
            <>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Fecha</TableCell>
                      <TableCell>{tab === 'materials' ? 'Materia prima' : 'Producto'}</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Motivo</TableCell>
                      <TableCell align="right">Cantidad</TableCell>
                      <TableCell align="right">Stock</TableCell>
                      {tab === 'materials' && <TableCell>Lote</TableCell>}
                      <TableCell>Referencia</TableCell>
                      <TableCell>Usuario</TableCell>
                      <TableCell>Notas</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map(r => (
                      <TableRow key={r.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDateTime(r.createdAt)}</TableCell>
                        <TableCell>{r.materialName || r.productName || '—'}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={r.type === 'IN' ? 'Entrada' : r.type === 'OUT' ? 'Salida' : 'Edición'}
                            color={r.type === 'IN' ? 'success' : r.type === 'OUT' ? 'default' : 'info'}
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title={r.reason}><span>{reasonLabel(r.reason)}</span></Tooltip>
                        </TableCell>
                        <TableCell align="right">{r.type === 'EDIT' ? '—' : `${r.type === 'IN' ? '+' : '−'}${r.quantity}`}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{r.previousStock} → {r.newStock}</TableCell>
                        {tab === 'materials' && <TableCell>{r.lotCode || '—'}</TableCell>}
                        <TableCell>{r.referenceNumber || '—'}</TableCell>
                        <TableCell>{r.userName || '—'}</TableCell>
                        <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.notes ? <Tooltip title={r.notes}><span>{r.notes}</span></Tooltip> : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {rows.length === 0 && (
                      <TableRow><TableCell colSpan={tab === 'materials' ? 10 : 9}><Typography variant="body2" color="text.secondary">Sin movimientos para los filtros elegidos.</Typography></TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">{totalCount} movimientos</Typography>
                <Pagination count={totalPages} page={page} onChange={(_, p) => load(p)} size="small" />
              </Stack>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default StockMovementsPage;
